import { useState } from 'react';
import { Moon, Sun, Globe, Bell, Shield, Trash2, LogOut, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useI18n } from '@/hooks/useI18n';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PATHS, PLANS } from '@/lib/index';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

export default function DashboardSettings() {
  const { profile, signOut, refreshProfile } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate(ROUTE_PATHS.HOME);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleChangePlan = async (newPlan: string) => {
    if (!profile) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ plan: newPlan })
        .eq('id', profile.id);

      if (error) throw error;

      await refreshProfile();
      toast({ title: t('success_generic'), description: t('settings_plan_updated') });
    } catch (error) {
      console.error('Plan update error:', error);
      toast({ title: t('error_generic'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: 'new_password_placeholder',
      });

      if (error) throw error;

      toast({ title: t('success_generic'), description: t('settings_password_changed') });
    } catch (error) {
      console.error('Password change error:', error);
      toast({ title: t('error_generic'), variant: 'destructive' });
    }
  };

  const handleDownloadData = () => {
    toast({ title: t('settings_download_data'), description: t('settings_download_data_info') });
  };

  const handleDeleteAccount = async () => {
    if (!profile) return;
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ kyc_status: 'rejected' })
        .eq('id', profile.id);

      if (error) throw error;

      toast({ title: t('success_generic'), description: t('settings_account_deleted') });
      await signOut();
      navigate(ROUTE_PATHS.HOME);
    } catch (error) {
      console.error('Delete account error:', error);
      toast({ title: t('error_generic'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const currentPlan = PLANS.find((p) => p.name === profile?.plan) || PLANS[0];

  return (
    <DashboardLayout title={t('settings_title')}>
      <div className="max-w-3xl mx-auto space-y-6 p-4">
        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold" /> {t('settings_account_settings')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">{t('settings_email')}</Label>
              <div className="p-3 bg-muted/30 rounded-lg">
                <p className="text-sm font-medium text-foreground">{profile?.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">{t('settings_password')}</Label>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start gap-2"
                onClick={handleChangePassword}
              >
                <Shield className="w-4 h-4" /> {t('settings_change_password')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold" /> {t('settings_account_plan')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('settings_current_plan')}</p>
                <p className="text-2xl font-bold text-foreground capitalize">{profile?.plan ?? 'Standard'}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {currentPlan.price === '0' ? t('settings_plan_free') : `£${currentPlan.price}/${t('settings_plan_month')}`}
                </p>
              </div>
              <Button variant="outline" className="shrink-0" asChild>
                <a href="/#plans">{t('settings_view_plans')}</a>
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {PLANS.map((plan) => (
                <Button
                  key={plan.name}
                  variant={profile?.plan === plan.name ? 'default' : 'outline'}
                  size="sm"
                  className={`text-xs ${profile?.plan === plan.name ? 'bg-gold text-primary-foreground' : ''}`}
                  onClick={() => handleChangePlan(plan.name)}
                  disabled={loading || profile?.plan === plan.name}
                >
                  {plan.name}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Globe className="w-5 h-5 text-gold" /> {t('settings_preferences')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                {isDark ? <Moon className="w-5 h-5 text-gold" /> : <Sun className="w-5 h-5 text-gold" />}
                <div>
                  <p className="text-sm font-medium text-foreground">{t('settings_theme')}</p>
                  <p className="text-xs text-muted-foreground">{isDark ? t('theme_dark') : t('theme_light')}</p>
                </div>
              </div>
              <Switch checked={isDark} onCheckedChange={toggleTheme} />
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <Globe className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium text-foreground">{t('settings_language')}</p>
                  <p className="text-xs text-muted-foreground">{lang === 'en' ? 'English (GB)' : 'Polski (PL)'}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant={lang === 'en' ? 'default' : 'ghost'}
                  size="sm"
                  className={`text-xs px-3 ${lang === 'en' ? 'bg-gold text-primary-foreground' : ''}`}
                  onClick={() => setLang('en')}
                >
                  EN
                </Button>
                <Button
                  variant={lang === 'pl' ? 'default' : 'ghost'}
                  size="sm"
                  className={`text-xs px-3 ${lang === 'pl' ? 'bg-gold text-primary-foreground' : ''}`}
                  onClick={() => setLang('pl')}
                >
                  PL
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold text-muted-foreground">£</span>
                <div>
                  <p className="text-sm font-medium text-foreground">{t('settings_currency')}</p>
                  <p className="text-xs text-muted-foreground">British Pound (GBP)</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-muted-foreground gap-1" disabled>
                {t('settings_change')} <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Bell className="w-5 h-5 text-gold" /> {t('settings_notifications')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-foreground cursor-pointer" htmlFor="email-notifications">
                  {t('settings_email_notifications')}
                </Label>
                <p className="text-xs text-muted-foreground">{t('settings_email_notifications_desc')}</p>
              </div>
              <Switch
                id="email-notifications"
                checked={emailNotifications}
                onCheckedChange={setEmailNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-foreground cursor-pointer" htmlFor="push-notifications">
                  {t('settings_push_notifications')}
                </Label>
                <p className="text-xs text-muted-foreground">{t('settings_push_notifications_desc')}</p>
              </div>
              <Switch
                id="push-notifications"
                checked={pushNotifications}
                onCheckedChange={setPushNotifications}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm font-medium text-foreground cursor-pointer" htmlFor="sms-notifications">
                  {t('settings_sms_notifications')}
                </Label>
                <p className="text-xs text-muted-foreground">{t('settings_sms_notifications_desc')}</p>
              </div>
              <Switch
                id="sms-notifications"
                checked={smsNotifications}
                onCheckedChange={setSmsNotifications}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold" /> {t('settings_security')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-foreground gap-2"
              disabled
            >
              <Shield className="w-4 h-4" /> {t('settings_2fa')} <span className="text-xs text-muted-foreground ml-auto">{t('settings_coming_soon')}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-foreground gap-2"
              disabled
            >
              <Shield className="w-4 h-4" /> {t('settings_session_management')} <span className="text-xs text-muted-foreground ml-auto">{t('settings_coming_soon')}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-foreground gap-2"
              disabled
            >
              <Shield className="w-4 h-4" /> {t('settings_login_history')} <span className="text-xs text-muted-foreground ml-auto">{t('settings_coming_soon')}</span>
            </Button>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-5 h-5 text-gold" /> {t('settings_privacy_data')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground hover:text-foreground gap-2"
              onClick={handleDownloadData}
            >
              <Shield className="w-4 h-4" /> {t('settings_download_data')}
            </Button>
          </CardContent>
        </Card>

        <Card className="border-destructive/30 bg-card shadow-lg">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" /> {t('settings_danger_zone')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-2"
                >
                  <Trash2 className="w-4 h-4" /> {t('settings_delete_account')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('settings_delete_account_confirm')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('settings_delete_account_warning')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('btn_cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteAccount}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    disabled={loading}
                  >
                    {t('btn_confirm')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </CardContent>
        </Card>

        <Card className="border-border/50 bg-card shadow-lg">
          <CardContent className="p-4">
            <Button
              variant="outline"
              className="w-full border-destructive/30 text-destructive hover:bg-destructive/10 gap-2"
              onClick={handleSignOut}
            >
              <LogOut className="w-4 h-4" /> {t('logout')}
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground py-4">
          CYF24 Bank · {t('legal_fca')} · {t('legal_fscs')} · © 2026
        </p>
      </div>
    </DashboardLayout>
  );
}
